/**
 * MenuBuilderPage — strona /menu-builder (tenant scope, ADR-0031).
 *
 * Po Commit 1 (shell extraction) — cienki wrapper nad `MenuBuilderShell`
 * z tenant providerami (apiPrefix `/menu`, i18nPrefix `tenant.menuLayoutBuilder`,
 * tenant query keys, useMenuLayouts/useMenuSource, SYSTEM_ITEMS jako systemItemDefinitions).
 *
 * Cała logika stanu/mutacji/render żyje w MenuBuilderShell — admin scope (ADR-0034)
 * reużywa ten sam shell z admin providerami w `AdminMenuBuilderPage`.
 *
 * „Przypisz layout do ról" NIE jest tu podawany (F24): u tenanta menu przypisuje się per
 * user/rola/zespół w Centrum uprawnień (zakładka „Menu"), więc shell chowa ten przycisk.
 */

import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  MENU_FUNCTION_MODULE_CODES,
  type MenuExportFile,
  SYSTEM_ITEMS,
  type SystemItemDefinition,
} from '@saas/shared';
import { useQueryClient } from '@tanstack/react-query';

import { MenuImportModal } from '@/components/menu/MenuImportModal';
import {
  MenuBuilderShell,
  type MenuBuilderShellQueryKeys,
} from '@/components/menu-builder-shell/MenuBuilderShell';
import {
  groupInstalledItemsByModule,
  pruneEmptySections,
  stripPinnedBaseDuplicates,
  useInstalledModulesMenuQuery,
} from '@/components/Sidebar';
import { useTenantAuth } from '@/contexts/TenantAuthContext';
import { useToast } from '@/contexts/ToastContext';
import { ACTIVE_MENU_LAYOUT_QUERY_KEY } from '@/hooks/useActiveMenuLayout';
import { MENU_LAYOUTS_QUERY_KEY, useMenuLayouts } from '@/hooks/useMenuLayouts';
import { AVAILABLE_MENU_LAYOUTS_KEY } from '@/hooks/useMenuPreference';
import { MENU_SOURCE_QUERY_KEY, useMenuSource } from '@/hooks/useMenuSource';
import { api } from '@/lib/api';
import { downloadJson, toFileNamePart } from '@/lib/download-file';
import {
  convertInstalledItemsToMenuItems,
  countInstalledLeaves,
} from '@/lib/installed-menu-convert';
import {
  useTenantKartotekaLayoutActions,
  useTenantKartotekaViews,
  useTenantMenuPreviewResolver,
} from '@/lib/preview/tenant-menu-preview';

const TENANT_QUERY_KEYS: MenuBuilderShellQueryKeys = {
  activeLayout: ACTIVE_MENU_LAYOUT_QUERY_KEY,
  layoutsList: MENU_LAYOUTS_QUERY_KEY,
  availableLayouts: AVAILABLE_MENU_LAYOUTS_KEY,
  layoutById: (id) => ['tenant', 'menu', 'layouts', id] as const,
};

const TENANT_SYSTEM_ITEMS_MAP: ReadonlyMap<string, SystemItemDefinition> = new Map(
  SYSTEM_ITEMS.map((s) => [s.itemKey as string, s]),
);

export function MenuBuilderPage() {
  // Menu modułów do sekcji read-only galerii (makieta v2): drzewo zainstalowanych
  // obszarów zgrupowane po module + fragment przełożony na MenuItem[] (Podgląd/Duplikuj).
  // Moduły-FUNKCJE (Procesy, KSeF) wykluczone — to jeden link, nie kartoteka, więc nie są
  // szablonem menu do zduplikowania (decyzja 2026-08-01, wspólna stała z sidebarem). Kartoteki
  // dokumentów też się tu nie pojawiają — należą do pakietu bazowego, dodaje się je z palety
  // (moduły doc_* wycofane z katalogu). Efekt: świeży tenant widzi jedno „Systemowe";
  // moduły HR (realne kartoteki) zostają jako osobne menu.
  const installedQuery = useInstalledModulesMenuQuery();
  const moduleMenus = useMemo(
    () =>
      groupInstalledItemsByModule(installedQuery.data ?? [])
        .filter((group) => !MENU_FUNCTION_MODULE_CODES.has(group.code))
        .map((group) => {
          // Parytet z realnym sidebarem tenanta (model „widok = moduł"): odsiewamy
          // duplikaty bazy stałej (Użytkownicy/Firmy/Ustawienia — żyją w stopce) i
          // przycinamy puste sekcje, zanim policzymy pozycje i złożymy makietę.
          const items = pruneEmptySections(stripPinnedBaseDuplicates(group.items));
          return {
            code: group.code,
            name: group.name,
            icon: group.icon,
            positions: countInstalledLeaves(items),
            hierarchy: convertInstalledItemsToMenuItems(items),
          };
        }),
    [installedQuery.data],
  );

  // Zunifikowany podgląd menu (parytet z hubem): resolver pozycja → pola/layouty
  // kartoteki, na danych z /kartoteki/{slug}/definition (systemowe) i /modules/{slug}
  // (z Kreatora). Ten sam wynik useMenuSource co w shellu (wspólny cache).
  const sourceQuery = useMenuSource();
  const sourceKartoteki =
    sourceQuery.data && 'kartoteki' in sourceQuery.data ? sourceQuery.data.kartoteki : undefined;
  const resolvePreviewNode = useTenantMenuPreviewResolver(sourceKartoteki);
  // Akordeon pozycji (Ustawienia / Layout formularza / Widok list / Filtry stałe)
  // — syntetyczne encje z layoutów kartotek + resolver sluga (pozycje `module`).
  const kartotekaViews = useTenantKartotekaViews(sourceKartoteki);

  // „Dodaj kartotekę" z palety (wariant A, 2026-07-03): POST /modules tworzy kartotekę
  // własną (tabela + default layout + kategoria „Ogólne" + permissiony — backend F12),
  // invalidacja źródła palety, pozycja wpada do menu w palecie (onAdd w MenuPalette).
  // Gate: crm_core.custom_modules.manage — bez uprawnienia brak „+" (backend i tak 403).
  const { t } = useTranslation();
  const { me } = useTenantAuth();
  const queryClient = useQueryClient();
  const canManageCustomModules =
    me?.user.isSuperUser === true ||
    (me?.permissions.includes('crm_core.custom_modules.manage') ?? false);
  const canManageSystemModules =
    me?.user.isSuperUser === true ||
    (me?.permissions.includes('crm_core.system_modules.manage') ?? false);
  // Akcje layoutów kartotek z akordeonu (etap b cz.2): + Nowy / Duplikuj / Usuń
  // w sekcjach Layout formularza i Widok list — POST/DELETE na endpointy layoutów
  // (custom: /modules/{id}/layouts, systemowe: /system-modules/{slug}/layouts).
  const kartotekaLayoutActions = useTenantKartotekaLayoutActions(sourceKartoteki, {
    canManageCustom: canManageCustomModules,
    canManageSystem: canManageSystemModules,
  });
  // Eksport/import menu do pliku (przenoszenie menu między tenantami). Gate jak zapis menu:
  // crm_core.menu_layouts.edit — bez uprawnienia przyciski ukryte (backend i tak 403).
  const toast = useToast();
  const canEditMenuLayouts =
    me?.user.isSuperUser === true ||
    (me?.permissions.includes('crm_core.menu_layouts.edit') ?? false);
  const exportMenu = useCallback(
    async (layoutId: string) => {
      try {
        const res = await api.get<MenuExportFile>(`/menu/layouts/${layoutId}/export`);
        downloadJson(res.data, `menu-${toFileNamePart(res.data.menu.name, 'menu')}.json`);
        toast.show({
          variant: 'success',
          title: t('tenant.menuLayoutBuilder.export.done', { name: res.data.menu.name }),
        });
      } catch {
        toast.show({ variant: 'error', title: t('tenant.menuLayoutBuilder.export.error') });
      }
    },
    [t, toast],
  );
  const menuTransfer = useMemo(
    () =>
      canEditMenuLayouts
        ? {
            onExport: (layoutId: string) => void exportMenu(layoutId),
            ImportModal: MenuImportModal,
          }
        : undefined,
    [canEditMenuLayouts, exportMenu],
  );

  const createKartoteka = useCallback(
    async (name: string) => {
      try {
        const res = await api.post<{ module: { slug: string; name: string } }>('/modules', {
          name,
        });
        await queryClient.invalidateQueries({ queryKey: MENU_SOURCE_QUERY_KEY });
        return { slug: res.data.module.slug, label: res.data.module.name };
      } catch (e) {
        const code = (e as { response?: { data?: { error?: string } } }).response?.data?.error;
        throw new Error(
          code === 'MODULE_LIMIT_EXCEEDED'
            ? t('tenant.menuLayoutBuilder.palette.kartotekaCreateErrorLimit', {
                defaultValue: 'Osiągnięto limit kartotek własnych.',
              })
            : t('tenant.menuLayoutBuilder.palette.kartotekaCreateErrorGeneric', {
                defaultValue: 'Nie udało się utworzyć kartoteki.',
              }),
          { cause: e },
        );
      }
    },
    [queryClient, t],
  );

  return (
    <MenuBuilderShell
      apiPrefix="/menu"
      i18nPrefix="tenant.menuLayoutBuilder"
      backUrl="/settings"
      allowModuleType
      queryKeys={TENANT_QUERY_KEYS}
      useLayoutsHook={useMenuLayouts}
      useSourceHook={useMenuSource}
      systemItemDefinitions={TENANT_SYSTEM_ITEMS_MAP}
      moduleMenus={moduleMenus}
      paneMode="list"
      resolvePreviewNode={resolvePreviewNode}
      kartotekaViews={kartotekaViews}
      onCreateKartoteka={canManageCustomModules ? createKartoteka : undefined}
      kartotekaLayoutActions={kartotekaLayoutActions}
      enablePaletteDnd
      menuTransfer={menuTransfer}
    />
  );
}
